import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";

// Components
import LoginForm from "~/components/LoginForm/LoginForm";

// Styles
import s from "./LoginPage.module.scss";
import GovernmentBuilding from "~/assets/icons/government-building.svg";
import PrivateBuilding from "~/assets/icons/private-building.svg";

// Types
import RootStore from "~/store/RootStore";
import { INewPatientForm, INewPatientPayload } from "~/types/patient";
import { Sex } from "~/types/patient";

// Hooks
import { useStore } from "~/hooks/useStore";
import ErrorNotAbleToAssign from "~/components/modals/error/ErrorNotAbleToAssign";

// Utils
import { serializeFormData } from "~/utils/helpers";
import { logEvent } from "~/utils/amplitude";
import ROUTES from "~/utils/routes";

interface Props {}

const LoginPage: React.FC<Props> = observer(({}) => {
  const store: RootStore = useStore();
  const regionId = store.cityStore.currentCity?.area_code ?? null;
  const patient = store.patientStore.currentPatient;
  const navigate = useNavigate();

  useEffect(() => {
    logEvent("Э/ Страница авторизации пациента");
  }, []);

  const login = (payload: INewPatientForm, onSubmitCallback: () => void) => {
    logEvent("Д/ Нажал кнопку Войти / Авторизация пациента");

    if (!regionId) {
      return;
    }

    const extendedFormData = serializeFormData<
      INewPatientForm,
      INewPatientPayload
    >(payload, (initialFormData) => {
      delete initialFormData.personal;

      return {
        ...initialFormData,
        id_gender: +payload.id_gender as Sex,
        id_region: regionId,
      };
    });

    store.patientStore
      .createPatient(extendedFormData)
      .then((data) => {
        if (!data || !data.response || store.egisErrorStore.isEgisError) {
          return;
        }

        store.patientStore.setCurrentPatient({
          ...payload,
          idUserData: data.response.idUserData,
          id_gender: +payload.id_gender as Sex,
        });

        navigate(
          store.cityStore.isMoscowRegion
            ? ROUTES.SPECIALTIES.PATHNAME
            : ROUTES.HOSPITALS.PATHNAME,
          { replace: true }
        );
      })
      .catch((e) => {
        logEvent("О/ Ошибка авторизации", {
          "Сообщение в ошибке": e.message,
        });
      })
      .finally(onSubmitCallback);
  };

  const handleClose = useCallback(() => {
    logEvent("Д/ Закрыл окно / Ошибка авторизации");
    store.egisErrorStore.setIsEgisErrorRelatedToAssign(false);
  }, [store.egisErrorStore]);

  return (
    <div className="screen-container">
      <div className={s.loginContainer}>
        <h1 className={"headline-1 " + s.title}>Авторизация пациента</h1>

        <p className={"lead-text " + s.lead}>
          Запись в государственные и частные больницы возможна после проверки
          данных пациента
        </p>

        <div className={s.loginForm}>
          <div className={s.loginFormItself}>
            <div className={s.container}>
              <LoginForm
                regionId={regionId}
                loginCallback={login}
                savedPatient={patient}
              />
            </div>
          </div>

          <div className={s.loginFormInformation}>
            <div className={s.container}>
              <div className={s.block}>
                <p className={s.header}>Обратите внимание!</p>
                <p className={s.text}>
                  Чтобы посторонний человек не совершил запись в медицинскую
                  организацию от вашего имени - введена обязательная авторизация
                  для обращения в медицинские организации.
                </p>
              </div>

              <div className={s.block}>
                <p className={s.header}>На портале Вам будут доступны:</p>
                <div className={s.features}>
                  <div className={s.feature}>
                    <div className={s.featureIcon}>
                      <img src={GovernmentBuilding} />
                    </div>
                    <span>Государственные медицинские учреждения</span>
                  </div>
                  <div className={s.feature}>
                    <div className={s.featureIcon}>
                      <img src={PrivateBuilding} />
                    </div>
                    <span>Частные медицинские центры</span>
                  </div>
                </div>
              </div>

              <div className={s.block}>
                <p className={s.text}>
                  В случае затруднений при входе на сайт после смены страхового
                  медицинского полиса или паспорта, обратитесь в вашу
                  поликлинику с просьбой изменить в базе номер полиса ОМС или
                  данные паспорта.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ErrorNotAbleToAssign
        isError={store.egisErrorStore.isEgisErrorRelatedToAssign}
        onClose={handleClose}
      />
    </div>
  );
});

export default LoginPage;
